import { useState } from 'react';
import { Search, AlertCircle, RefreshCw, X } from 'lucide-react';
import BalanceCard from './BalanceCard';
import EventsList from './EventsList';
import PaymentHistory from './PaymentHistory';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export function MemberViewModal({ onClose }) {
  const [searchName, setSearchName] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [selectedMember, setSelectedMember] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [loadingMember, setLoadingMember] = useState(false);
  const [memberError, setMemberError] = useState(null);

  const handleSearch = async () => {
    if (searchName.trim().length < 2) return;
    setSearching(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const response = await fetch(
        `${API_URL}/api/admin/member-search?name=${encodeURIComponent(searchName.trim())}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Search failed');
      const data = await response.json();
      setSearchResults(data.results || []);
      if (data.results.length === 0) setSearchError('No members found.');
    } catch (err) {
      setSearchError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectMember = async (member) => {
    setSelectedMember(member);
    setSearchResults([]);
    setMemberData(null);
    setMemberError(null);
    setLoadingMember(true);

    try {
      const response = await fetch(
        `${API_URL}/api/admin/member-view/${member.id}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Failed to load member');
      const data = await response.json();
      setMemberData(data);
    } catch (err) {
      setMemberError('Failed to load member dashboard.');
    } finally {
      setLoadingMember(false);
    }
  };

  const handleReset = () => {
    setSelectedMember(null);
    setMemberData(null);
    setMemberError(null);
    setSearchName('');
    setSearchResults([]);
  };

  // ── Full screen dashboard view once member is loaded ──────────────────────
  if (memberData && !memberData.notInGroup) {
    return (
      <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
          <div>
            <p className="text-xs text-gray-500 font-medium">Admin — Member View</p>
            <p className="text-sm font-bold text-gray-800">{selectedMember.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="text-sm text-blue-600 hover:underline"
            >
              Search Again
            </button>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Dashboard content */}
       <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4">
          <BalanceCard dashboardData={memberData} />
          <EventsList
            currentEvents={memberData.currentEvents}
            historicalEvents={memberData.historicalEvents}
            requiredAmount={memberData.requiredAmount}
            startingBalance={memberData.startingBalance}
          />
          <PaymentHistory paymentHistory={memberData.paymentHistory} />
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-2xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // ── Floating card for search ───────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Member View</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
            ✕
          </button>
        </div>

        {/* Search input */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name..."
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            <button
              onClick={handleSearch}
              disabled={searching || searchName.trim().length < 2}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {searching
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Searching...</>
                : <><Search className="w-4 h-4" /> Search</>}
            </button>
          </div>

          {searchError && (
            <p className="text-sm text-gray-500">{searchError}</p>
          )}

          {searchResults.length > 0 && (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {searchResults.map(member => (
                <button
                  key={member.id}
                  onClick={() => handleSelectMember(member)}
                  className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors"
                >
                  <p className="font-medium text-gray-800 text-sm">{member.name}</p>
                  {member.email && (
                    <p className="text-xs text-gray-500 mt-0.5">{member.email}</p>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Loading */}
          {loadingMember && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-3" />
              <p className="text-gray-500 text-sm">Loading member dashboard...</p>
            </div>
          )}

          {/* Error */}
          {memberError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{memberError}</p>
            </div>
          )}

          {/* Not in group */}
          {memberData?.notInGroup && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0" />
              <p className="text-sm text-orange-800">{memberData.message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MemberViewModal;